// src/features/assessment/data/results.creator.ts

import { AssessmentResult } from '../engine/types';

export const CREATOR_RESULTS: Record<string, AssessmentResult> = {

  // ==========================================
  // RESULT 1: THE SOVEREIGN FOUNDER (Ideal Client)
  // ==========================================
  sovereign_founder: {
    id: 'sovereign_founder',
    track: 'creator',
    title: "The Sovereign Founder",
    verdict: "Greenfield Advantage",
    hero_image: "/images/illustrations/results/sovereign_hero.avif",
    
    diagnosis: {
      headline: "You have the rare opportunity to build an AI-Native entity from Day 1.",
      text: "Most companies struggle with AI because they have to 'untangle' years of bad data, messy permissions, and legacy habits. You don't. You can skip the 'Digital Transformation' phase entirely. Your biggest risk right now is following the old playbook: hiring a bloated human team to do work that software should do."
    },
    
    solution: {
      headline: "Do not hire a bloated team.",
      text: "Hire our **Dedicated Engineering Unit** to build your **Company AI Computer**. We install the Canvas OS foundation in 14 days, giving you the output of a Series A company with a Seed stage headcount. You don't have to 'retrain' humans to use AI; your workforce is AI-First by design."
    },

    // Creator track is holistic, so we use 'default' for the roadmap
    dynamic_content: {
      default: {
        title: "The 3-Month Build",
        objective: "Achieve Series A output with Seed stage headcount.",
        roadmap_title: "The Launch Roadmap",
        steps: [
          { phase: "Days 1-14", name: "The Foundation", detail: "Install Canvas OS and deploy C-Suite Agents (Strategy, Architect)." },
          { phase: "Month 1-2", name: "Custom Workflows", detail: "Build specific Sales, Marketing, and Dev agents tailored to your business logic." },
          { phase: "Month 3", name: "Handover", detail: "You own the IP. Your 'workforce' is live. You only hire 3-12 key humans to direct the flow." }
        ],
        read_more: { title: "Read: The End of SaaS", slug: "the-end-of-saas" }
      },
      // Fallbacks for type safety
      engineering: { title: "", objective: "", roadmap_title: "", steps: [] },
      sales: { title: "", objective: "", roadmap_title: "", steps: [] },
      marketing: { title: "", objective: "", roadmap_title: "", steps: [] },
      ops: { title: "", objective: "", roadmap_title: "", steps: [] }
    },

    intelligence: {
      title: "Recommended Intelligence",
      articles: [
        { title: "The 48-Hour Blueprint", subtitle: "How we map your future.", slug: "48-hour-blueprint-ai-audit" },
        { title: "The End of SaaS", subtitle: "Why you need a central computer.", slug: "the-end-of-saas" }
      ]
    },

    cta: {
      primary: { label: "Book Setup Sprint", variant: "book", cal_params: { note: "Outcome: Sovereign Founder" } },
      secondary: { label: "Save Results", variant: "save" }
    }
  },

  // ==========================================
  // RESULT 2: THE TRADITIONAL BUILDER (Risk Warning)
  // ==========================================
  traditional_builder: {
    id: 'traditional_builder',
    track: 'creator',
    title: "The Traditional Builder",
    verdict: "High Risk of 'Process Debt'",
    hero_image: "/images/illustrations/results/traditional_hero.avif",
    
    diagnosis: {
      headline: "You are defaulting to the 'Standard Playbook', which is a liability in 2025.",
      text: "You prioritize headcount growth (aiming for 50+ people) or renting standard SaaS tools. While this feels safe, you are building a Human-Centric organization. Every human you hire adds communication overhead. Every SaaS you rent adds a data silo."
    },
    
    solution: {
      headline: "Automate Before You Scale",
      text: "If you hire 20 people without a standardized operating system, you will spend the next 2 years managing communication overhead rather than shipping product. We recommend adopting **Canvas OS** as your operational backbone *before* you add bodies."
    },

    dynamic_content: {
      default: {
        title: "The Pivot Plan",
        objective: "Avoid the 'Management Tax' of a large team.",
        roadmap_title: "Lean Leverage Roadmap",
        steps: [
          { phase: "Step 1", name: "Freeze Headcount", detail: "Do not hire the 'Junior Ops' or 'SDR' roles yet." },
          { phase: "Step 2", name: "Install OS", detail: "Adopt Canvas OS to force structured data habits (Linear/Slack/Notion) from Day 1." },
          { phase: "Step 3", name: "Hire Leaders", detail: "Hire a Head of Sales who wants to use AI, not one who wants to hire 10 reps." }
        ],
        read_more: { title: "Read: The 2025 AI Divide", slug: "ai-readiness-gap-pilot-purgatory" }
      },
      engineering: { title: "", objective: "", roadmap_title: "", steps: [] },
      sales: { title: "", objective: "", roadmap_title: "", steps: [] },
      marketing: { title: "", objective: "", roadmap_title: "", steps: [] },
      ops: { title: "", objective: "", roadmap_title: "", steps: [] }
    },

    intelligence: {
      title: "Recommended Intelligence",
      articles: [
        { title: "The End of SaaS", subtitle: "Why renting tools slows you down.", slug: "the-end-of-saas" },
        { title: "The 2025 AI Divide", subtitle: "Why hiring more people fails.", slug: "ai-readiness-gap-pilot-purgatory" }
      ]
    },

    cta: {
      primary: { label: "Book Strategy Call", variant: "consultation", cal_params: { note: "Outcome: Traditionalist Pivot" } },
      secondary: { label: "Join Newsletter", variant: "newsletter" }
    }
  },

  // ==========================================
  // RESULT 3: THE BOOTSTRAPPER (Not Ready)
  // ==========================================
  bootstrapper: {
    id: 'bootstrapper',
    track: 'creator',
    title: "The Bootstrapper",
    verdict: "Survival Mode",
    hero_image: "/images/illustrations/results/bootstrapper_hero.avif",
    
    diagnosis: {
      headline: "Focus on cash flow, not infrastructure.",
      text: "Your focus is rightly on extending runway and minimizing burn. This is a valid and necessary stage, but it is not the stage for Sovereign AI Infrastructure (Private AI Servers, Custom AI Agents). You need Traction, not Leverage."
    },
    
    solution: {
      headline: "The 'Manual' AI Stack",
      text: "Do not hire us yet. Do not hire anyone yet. Use the 'Prosumer' stack (ChatGPT Plus, Claude, Zapier) to augment your own labor for $20/month. Come back to us when you hit $10k MRR."
    },

    dynamic_content: {
      default: {
        title: "Immediate Focus",
        objective: "Reach default alive state.",
        roadmap_title: "The Hustle Roadmap",
        steps: [
          { phase: "Phase 1", name: "The Founder-Agent", detail: "Use ChatGPT to draft every email and code snippet." },
          { phase: "Phase 2", name: "No-Code", detail: "Use Zapier/Make to connect your forms to email. Don't build custom software." },
          { phase: "Phase 3", name: "Sales Grind", detail: "Do things that don't scale until you have revenue." }
        ],
        read_more: { title: "Read: The End of SaaS", slug: "the-end-of-saas" }
      },
      engineering: { title: "", objective: "", roadmap_title: "", steps: [] },
      sales: { title: "", objective: "", roadmap_title: "", steps: [] },
      marketing: { title: "", objective: "", roadmap_title: "", steps: [] },
      ops: { title: "", objective: "", roadmap_title: "", steps: [] }
    },

    intelligence: {
      title: "Recommended Intelligence",
      articles: [
        { title: "The End of SaaS", subtitle: "Understand the end-game.", slug: "the-end-of-saas" }
      ]
    },

    cta: {
      primary: { label: "Get Free Founder Guides", variant: "newsletter" },
      secondary: { label: "Join Newsletter", variant: "newsletter" }
    }
  }
}; 